import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
  InputAdornment,
  alpha,
  styled,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  CardGiftcard as MembershipIcon,
  CheckCircle as VerifiedIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const colors = {
  gold: '#C5A572',
  burgundy: '#800020',
  navy: '#1A1F2E',
  cream: '#FDFAF3',
  emerald: '#046307',
};

const StyledPaper = styled(Paper)({
  background: `linear-gradient(135deg, ${colors.cream} 0%, #FFFFFF 100%)`,
  border: `1px solid ${alpha(colors.gold, 0.3)}`,
  borderRadius: '16px',
  padding: '24px',
  boxShadow: `0 10px 30px -10px ${alpha(colors.gold, 0.2)}`,
});

const LuxuryTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    '&:hover': {
      backgroundColor: '#FFFFFF',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: colors.gold,
      borderWidth: '2px',
    },
  },
});

const PaymentButton = styled(Button)(({ variant }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontWeight: 600,
  textTransform: 'uppercase',
  ...(variant === 'contained' && {
    background: `linear-gradient(135deg, ${colors.burgundy} 0%, ${colors.navy} 100%)`,
    color: colors.cream,
    '&:hover': {
      background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.burgundy} 100%)`,
    },
  }),
  ...(variant === 'outlined' && {
    border: `2px solid ${colors.gold}`,
    color: colors.navy,
    '&:hover': {
      background: alpha(colors.gold, 0.1),
    },
  }),
}));

interface PaymentSectionProps {
  totalAmount: number;
  discountAmount: number;
  paymentMethod: string;
  setPaymentMethod: (method: string) => void;
  membershipId: string;
  setMembershipId: (id: string) => void;
  membershipVerified: boolean;
  onVerifyMembership: () => void;
}

export const PaymentSection: React.FC<PaymentSectionProps> = ({
  totalAmount,
  discountAmount,
  paymentMethod,
  setPaymentMethod,
  membershipId,
  setMembershipId,
  membershipVerified,
  onVerifyMembership,
}) => {
  const finalAmount = totalAmount - discountAmount;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <StyledPaper>
        {/* Membership Section */}
        <Box mb={3}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.navy, mb: 2 }}>
            <MembershipIcon sx={{ color: colors.gold }} />
            Membership Verification
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <LuxuryTextField
              label="Membership ID"
              value={membershipId}
              onChange={(e) => setMembershipId(e.target.value)}
              disabled={membershipVerified}
              fullWidth
              InputProps={{
                endAdornment: membershipVerified && (
                  <InputAdornment position="end">
                    <VerifiedIcon sx={{ color: colors.emerald }} />
                  </InputAdornment>
                ),
              }}
            />
            <PaymentButton
              variant="outlined"
              onClick={onVerifyMembership}
              disabled={!membershipId || membershipVerified}
            >
              Verify
            </PaymentButton>
          </Box>
          {membershipVerified && (
            <Typography variant="body2" sx={{ color: colors.emerald, mt: 1 }}>
              Membership verified! Discount applied.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3, borderColor: alpha(colors.gold, 0.3) }} />

        {/* Payment Method */}
        <Box mb={3}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: colors.navy, mb: 2 }}>
            <PaymentIcon sx={{ color: colors.gold }} />
            Payment Method
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <FormControlLabel
                value="CASH"
                control={<Radio sx={{ color: colors.gold, '&.Mui-checked': { color: colors.burgundy } }} />}
                label="Cash"
              />
              <FormControlLabel
                value="CARD"
                control={<Radio sx={{ color: colors.gold, '&.Mui-checked': { color: colors.burgundy } }} />}
                label="Card"
              />
              <FormControlLabel
                value="UPI"
                control={<Radio sx={{ color: colors.gold, '&.Mui-checked': { color: colors.burgundy } }} />}
                label="UPI"
              />
              <FormControlLabel
                value="INSURANCE"
                control={<Radio sx={{ color: colors.gold, '&.Mui-checked': { color: colors.burgundy } }} />}
                label="Insurance"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3, borderColor: alpha(colors.gold, 0.3) }} />

        {/* Amount Summary */}
        <Box>
          <Typography variant="h6" sx={{ color: colors.navy, mb: 2 }}>
            Payment Summary
          </Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body1">Total Amount:</Typography>
            <Typography variant="body1">₹{totalAmount}</Typography>
          </Box>
          {discountAmount > 0 && (
            <Box display="flex" justifyContent="space-between" mb={1} sx={{ color: colors.burgundy }}>
              <Typography variant="body1">Discount:</Typography>
              <Typography variant="body1">- ₹{discountAmount}</Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="space-between" mt={2}>
            <Typography variant="h6" sx={{ color: colors.navy }}>Final Amount:</Typography>
            <Typography variant="h6" sx={{ color: colors.burgundy }}>₹{finalAmount}</Typography>
          </Box>
        </Box>
      </StyledPaper>
    </motion.div>
  );
};

// import React from 'react';
// import {
//   Box,
//   Paper,
//   Typography,
//   TextField,
//   Button,
//   Grid,
//   Divider,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   FormControl,
//   FormLabel,
//   Alert,
// } from '@mui/material';
// import {
//   CreditCard as CardIcon,
//   AccountBalance as CashIcon,
//   QrCode as QrCodeIcon,
//   Verified as VerifiedIcon,
//   Discount as DiscountIcon,
// } from '@mui/icons-material';
// import { QRCodeSVG } from 'qrcode.react';

// interface PaymentSectionProps {
//   totalAmount: number;
//   discountAmount: number;
//   paymentMethod: string;
//   setPaymentMethod: (method: string) => void;
//   membershipId: string;
//   setMembershipId: (id: string) => void;
//   membershipVerified: boolean;
//   onVerifyMembership: () => void;
// }

// export const PaymentSection: React.FC<PaymentSectionProps> = ({
//   totalAmount,
//   discountAmount,
//   paymentMethod,
//   setPaymentMethod,
//   membershipId,
//   setMembershipId,
//   membershipVerified,
//   onVerifyMembership,
// }) => {
//   const netAmount = totalAmount - discountAmount;

//   return (
//     <Grid container spacing={3}>
//       <Grid size={{ xs: 12, md: 6 }}>
//         <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
//           <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <DiscountIcon color="primary" />
//             Membership Discount
//           </Typography>

//           <Box display="flex" gap={1} mb={2}>
//             <TextField
//               fullWidth
//               size="small"
//               placeholder="Enter membership ID"
//               value={membershipId}
//               onChange={(e) => setMembershipId(e.target.value)}
//               disabled={membershipVerified}
//             />
//             <Button
//               variant="contained"
//               onClick={onVerifyMembership}
//               disabled={!membershipId || membershipVerified}
//             >
//               Verify
//             </Button>
//           </Box>

//           {membershipVerified && (
//             <Alert severity="success" icon={<VerifiedIcon />}>
//               Membership verified! Discount applied.
//             </Alert>
//           )}
//         </Paper>

//         <Paper variant="outlined" sx={{ p: 2 }}>
//           <Typography variant="subtitle1" gutterBottom>
//             Payment Summary
//           </Typography>

//           <Box display="flex" justifyContent="space-between" mb={1}>
//             <Typography variant="body2">Sub Total:</Typography>
//             <Typography variant="body2">₹{totalAmount}</Typography>
//           </Box>

//           <Box display="flex" justifyContent="space-between" mb={1}>
//             <Typography variant="body2">Discount:</Typography>
//             <Typography variant="body2" color="error">-₹{discountAmount}</Typography>
//           </Box>

//           <Divider sx={{ my: 1 }} />

//           <Box display="flex" justifyContent="space-between" mb={2}>
//             <Typography variant="h6">Net Amount:</Typography>
//             <Typography variant="h6" color="primary">₹{netAmount}</Typography>
//           </Box>

//           <FormControl component="fieldset">
//             <FormLabel component="legend">Payment Method</FormLabel>
//             <RadioGroup
//               value={paymentMethod}
//               onChange={(e) => setPaymentMethod(e.target.value)}
//             >
//               <FormControlLabel 
//                 value="CASH" 
//                 control={<Radio />} 
//                 label={
//                   <Box display="flex" alignItems="center" gap={1}>
//                     <CashIcon /> Cash
//                   </Box>
//                 } 
//               />
//               <FormControlLabel 
//                 value="CARD" 
//                 control={<Radio />} 
//                 label={
//                   <Box display="flex" alignItems="center" gap={1}>
//                     <CardIcon /> Card
//                   </Box>
//                 } 
//               />
//               <FormControlLabel 
//                 value="UPI" 
//                 control={<Radio />} 
//                 label={
//                   <Box display="flex" alignItems="center" gap={1}>
//                     <QrCodeIcon /> UPI
//                   </Box>
//                 } 
//               />
//             </RadioGroup>
//           </FormControl>
//         </Paper>
//       </Grid>

//       <Grid size={{ xs: 12, md: 6 }}>
//         <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
//           {paymentMethod === 'UPI' ? (
//             <>
//               <Typography variant="h6" gutterBottom>
//                 Scan QR Code to Pay
//               </Typography>
//               <Box sx={{ my: 2 }}>
//                 <QRCodeSVG
//                   value={`upi://pay?pa=lab@okhdfcbank&pn=DiagnosticLab&am=${netAmount}&cu=INR`}
//                   size={200}
//                 />
//               </Box>
//               <Typography variant="body2" color="textSecondary">
//                 Amount: ₹{netAmount}
//               </Typography>
//               <Typography variant="caption" color="textSecondary">
//                 Scan using any UPI app
//               </Typography>
//             </>
//           ) : paymentMethod === 'CARD' ? (
//             <>
//               <Typography variant="h6" gutterBottom>
//                 Card Payment
//               </Typography>
//               <CardIcon sx={{ fontSize: 64, color: 'primary.main', my: 2 }} />
//               <Typography variant="body2">
//                 Swipe or insert card in the terminal
//               </Typography>
//             </>
//           ) : (
//             <>
//               <Typography variant="h6" gutterBottom>
//                 Cash Payment
//               </Typography>
//               <CashIcon sx={{ fontSize: 64, color: 'success.main', my: 2 }} />
//               <Typography variant="body2">
//                 Amount to collect: ₹{netAmount}
//               </Typography>
//             </>
//           )}
//         </Paper>
//       </Grid>
//     </Grid>
//   );
// };

// import React from 'react';
// import {
//   Box,
//   Paper,
//   Typography,
//   TextField,
//   Button,
//   Grid,
//   Divider,
//   RadioGroup,
//   FormControlLabel,
//   Radio,
//   FormControl,
//   FormLabel,
//   Alert,
// } from '@mui/material';
// import {
//   CreditCard as CardIcon,
//   AccountBalance as CashIcon,
//   QrCode as QrCodeIcon,
//   Verified as VerifiedIcon,
//   Discount as DiscountIcon,
// } from '@mui/icons-material';
// import { QRCodeSVG } from 'qrcode.react';

// interface PaymentSectionProps {
//   totalAmount: number;
//   discountAmount: number;
//   paymentMethod: string;
//   setPaymentMethod: (method: string) => void;
//   membershipId: string;
//   setMembershipId: (id: string) => void;
//   membershipVerified: boolean;
//   onVerifyMembership: () => void;
// }

// export const PaymentSection: React.FC<PaymentSectionProps> = ({
//   totalAmount,
//   discountAmount,
//   paymentMethod,
//   setPaymentMethod,
//   membershipId,
//   setMembershipId,
//   membershipVerified,
//   onVerifyMembership,
// }) => {
//   const netAmount = totalAmount - discountAmount;

//   return (
//     <Grid container spacing={3}>
//       {/* Left Column - Membership & Payment Summary */}
//       <Grid size={{ xs: 12 , md: 6 }} >
//         {/* Membership Discount Section */}
//         <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
//           <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//             <DiscountIcon color="primary" />
//             Membership Discount
//           </Typography>

//           <Box display="flex" gap={1} mb={2}>
//             <TextField
//               fullWidth
//               size="small"
//               placeholder="Enter membership ID"
//               value={membershipId}
//               onChange={(e) => setMembershipId(e.target.value)}
//               disabled={membershipVerified}
//             />
//             <Button
//               variant="contained"
//               onClick={onVerifyMembership}
//               disabled={!membershipId || membershipVerified}
//             >
//               Verify
//             </Button>
//           </Box>

//           {membershipVerified && (
//             <Alert severity="success" icon={<VerifiedIcon />}>
//               Membership verified! 22% discount applied.
//             </Alert>
//           )}
//         </Paper>

//         {/* Payment Summary Section */}
//         <Paper variant="outlined" sx={{ p: 2 }}>
//           <Typography variant="subtitle1" gutterBottom>
//             Payment Summary
//           </Typography>

//           <Box display="flex" justifyContent="space-between" mb={1}>
//             <Typography variant="body2">Sub Total:</Typography>
//             <Typography variant="body2">₹{totalAmount}</Typography>
//           </Box>

//           <Box display="flex" justifyContent="space-between" mb={1}>
//             <Typography variant="body2">Discount:</Typography>
//             <Typography variant="body2" color="error">-₹{discountAmount}</Typography>
//           </Box>

//           <Divider sx={{ my: 1 }} />

//           <Box display="flex" justifyContent="space-between" mb={2}>
//             <Typography variant="h6">Net Amount:</Typography>
//             <Typography variant="h6" color="primary">₹{netAmount}</Typography>
//           </Box>

//           <FormControl component="fieldset">
//             <FormLabel component="legend">Payment Method</FormLabel>
//             <RadioGroup
//               value={paymentMethod}
//               onChange={(e) => setPaymentMethod(e.target.value)}
//             >
//               <FormControlLabel 
//                 value="CASH" 
//                 control={<Radio />} 
//                 label={
//                   <Box display="flex" alignItems="center" gap={1}>
//                     <CashIcon /> Cash
//                   </Box>
//                 } 
//               />
//               <FormControlLabel 
//                 value="CARD" 
//                 control={<Radio />} 
//                 label={
//                   <Box display="flex" alignItems="center" gap={1}>
//                     <CardIcon /> Card
//                   </Box>
//                 } 
//               />
//               <FormControlLabel 
//                 value="UPI" 
//                 control={<Radio />} 
//                 label={
//                   <Box display="flex" alignItems="center" gap={1}>
//                     <QrCodeIcon /> UPI
//                   </Box>
//                 } 
//               />
//             </RadioGroup>
//           </FormControl>
//         </Paper>
//       </Grid>

//       {/* Right Column - Payment QR/Instructions */}
//       <Grid size={{ xs: 12, md: 6 }}>
//         <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
//           {paymentMethod === 'UPI' ? (
//             <>
//               <Typography variant="h6" gutterBottom>
//                 Scan QR Code to Pay
//               </Typography>
//               <Box sx={{ my: 2 }}>
//                 <QRCodeSVG
//                   value={`upi://pay?pa=lab@okhdfcbank&pn=DiagnosticLab&am=${netAmount}&cu=INR`}
//                   size={200}
//                 />
//               </Box>
//               <Typography variant="body2" color="textSecondary">
//                 Amount: ₹{netAmount}
//               </Typography>
//               <Typography variant="caption" color="textSecondary">
//                 Scan using any UPI app
//               </Typography>
//             </>
//           ) : paymentMethod === 'CARD' ? (
//             <>
//               <Typography variant="h6" gutterBottom>
//                 Card Payment
//               </Typography>
//               <CardIcon sx={{ fontSize: 64, color: 'primary.main', my: 2 }} />
//               <Typography variant="body2">
//                 Swipe or insert card in the terminal
//               </Typography>
//             </>
//           ) : (
//             <>
//               <Typography variant="h6" gutterBottom>
//                 Cash Payment
//               </Typography>
//               <CashIcon sx={{ fontSize: 64, color: 'success.main', my: 2 }} />
//               <Typography variant="body2">
//                 Amount to collect: ₹{netAmount}
//               </Typography>
//             </>
//           )}
//         </Paper>
//       </Grid>
//     </Grid>
//   );
// };